/**
 * The things an admin has to do something about, and nothing else.
 *
 * A card appears only when it has work in it, so an empty section means the
 * pipeline is clear rather than meaning the section failed to load. Each card
 * says what is stuck, how much, why, and what to do — a count with no next
 * step is a notification, not an action.
 */
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle2, FileSpreadsheet, Truck, Upload, Wallet, X } from 'lucide-react';
import { useRef, useState } from 'react';

import type { CodReconcileOutcome, CodReconcileResult } from '@terra/api-client';
import type { settlements } from '@terra/contracts';
import { Skeleton } from '@terra/ui';

import { api } from '../../lib/api';
import { formatPaise } from '../../lib/money';

import { parseRemittanceStatement, type ParsedStatement } from './remittanceStatement';
import { StagePill } from './StagePill';
import { TONES, type StageTone } from './tones';

interface CodRemittanceItem {
  id: string;
  subOrderId: string;
  orderNumber: string;
  vendorName: string;
  deliveredAt: string | null;
  amountPaise: number;
  awbNumber?: string | null;
  courierName?: string | null;
}

function ActionCard({
  icon: Icon,
  tone,
  title,
  amount,
  meta,
  why,
  action,
}: {
  icon: typeof Truck;
  tone: StageTone;
  title: string;
  amount: string;
  meta: string;
  why: string;
  action: React.ReactNode;
}) {
  const t = TONES[tone];
  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-2xl border p-5 shadow-sm ${t.card}`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${t.solid}`} aria-hidden />
      <div className="flex items-start gap-2.5">
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${t.chip}`}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className={`text-[13px] font-bold ${t.label}`}>{title}</p>
          <p className={`mt-1 text-[22px] font-bold tabular-nums leading-none ${t.amount}`}>
            {amount}
          </p>
          <p className="text-ink-muted mt-1 text-[12px] font-medium">{meta}</p>
        </div>
      </div>
      <p className="text-ink-muted mt-3 flex-1 text-[12.5px] font-medium leading-relaxed">{why}</p>
      <div className="mt-3">{action}</div>
    </div>
  );
}

export function ActionRequired({
  overview,
  codPending,
  codTotal,
  isLoading,
  onReviewCod,
}: {
  overview: settlements.AdminFinanceOverview | undefined;
  codPending: CodRemittanceItem[];
  codTotal: number;
  isLoading: boolean;
  onReviewCod: () => void;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-[170px] rounded-2xl" />
        <Skeleton className="h-[170px] rounded-2xl" />
      </div>
    );
  }

  const codAmount = codPending.reduce((sum, r) => sum + r.amountPaise, 0);
  const payoutsPending = overview?.payoutsPending;
  const cards: React.ReactNode[] = [];

  if (codTotal > 0) {
    cards.push(
      <ActionCard
        key="cod"
        icon={Truck}
        tone="orange"
        title="COD awaiting remittance"
        // The amount is what is on this page of pending legs; the count is the
        // true total, which is why they are labelled separately rather than
        // presented as one figure.
        amount={formatPaise(codAmount)}
        meta={`${codTotal} ${codTotal === 1 ? 'order' : 'orders'} pending${
          codPending.length < codTotal ? ` · ${codPending.length} shown` : ''
        }`}
        why="The courier collected cash from the customer and has not handed it over. Settlement for these orders cannot release until the money is marked received."
        action={
          <button
            type="button"
            onClick={onReviewCod}
            className="h-9 w-full rounded-lg bg-orange-600 text-[12.5px] font-bold text-white transition-colors hover:bg-orange-700"
          >
            Review orders
          </button>
        }
      />,
    );
  }

  if (payoutsPending && payoutsPending.count > 0) {
    cards.push(
      <ActionCard
        key="payouts"
        icon={Wallet}
        tone="violet"
        title="Payout requests awaiting a decision"
        amount={formatPaise(payoutsPending.paise)}
        meta={`${payoutsPending.count} ${payoutsPending.count === 1 ? 'request' : 'requests'}`}
        why="Sellers have asked to withdraw their balance. Each needs an admin to approve or reject it before any transfer happens."
        action={
          <p className="text-center text-[12px] font-semibold text-violet-800">
            Open a seller below to act on theirs
          </p>
        }
      />,
    );
  }

  return (
    <motion.section
      aria-label="Action required"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: 0.05 }}
    >
      <h2 className="text-ink-muted mb-3 text-[11px] font-bold uppercase tracking-wider">
        Action required
      </h2>
      {cards.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">{cards}</div>
      ) : (
        <div className={`flex items-center gap-3 rounded-2xl border p-5 ${TONES.emerald.card}`}>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="text-[13.5px] font-bold text-emerald-900">Everything is reconciled</p>
            <p className="text-[12.5px] font-medium text-emerald-800/80">
              No COD cash outstanding with couriers, and no payout request waiting on a decision.
            </p>
          </div>
        </div>
      )}
    </motion.section>
  );
}

// ── COD reconciliation ─────────────────────────────────────────────────────

const OUTCOME: Record<CodReconcileOutcome, { tone: StageTone; label: string; hint: string }> = {
  MATCHED: { tone: 'emerald', label: 'Matches', hint: 'Will be marked received' },
  AMOUNT_MISMATCH: {
    tone: 'rose',
    label: 'Amount differs',
    hint: 'Ask the courier before marking it',
  },
  ALREADY_RECEIVED: { tone: 'slate', label: 'Already received', hint: 'Nothing to do' },
  NOT_DELIVERED: {
    tone: 'amber',
    label: 'Not delivered yet',
    hint: 'We have no delivery for it — check the tracking',
  },
  NOT_FOUND: { tone: 'rose', label: 'Unknown parcel', hint: 'Not a COD parcel of ours' },
  DUPLICATE: { tone: 'slate', label: 'Repeated line', hint: 'Counted once' },
};

/**
 * Matches a courier remittance statement against what is owed, then marks the
 * exact matches received in one go under the courier's payment reference.
 */
function StatementReconciler({ onDone }: { onDone: () => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [reference, setReference] = useState('');
  const [parsed, setParsed] = useState<ParsedStatement | null>(null);
  const [result, setResult] = useState<CodReconcileResult | null>(null);

  const run = useMutation({
    mutationFn: (apply: boolean) =>
      api.settlements.reconcileCodRemittances({
        rows: parsed!.lines,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        apply,
      }),
    onSuccess: (r, apply) => {
      setResult(r);
      if (apply) onDone();
    },
  });

  const read = (value: string) => {
    setText(value);
    setResult(null);
    setParsed(value.trim() ? parseRemittanceStatement(value) : null);
  };

  const applied = result?.summary.applied ?? 0;
  const canApply = !!result && result.summary.matched > 0 && applied === 0;

  return (
    <div className="border-border/60 space-y-4 border-b bg-orange-50/30 px-5 py-5">
      <div className="grid gap-3 lg:grid-cols-[1fr_280px]">
        <div>
          <label className="text-ink mb-1.5 block text-[12px] font-bold" htmlFor="cod-statement">
            Remittance statement
          </label>
          <textarea
            id="cod-statement"
            value={text}
            onChange={(e) => read(e.target.value)}
            rows={5}
            placeholder={
              'Upload Shiprocket’s remittance CSV, or paste lines:\nAWB,Amount\n14344512345,520.00'
            }
            className="border-border bg-surface text-ink w-full rounded-xl border px-3 py-2 font-mono text-[12.5px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="border-border bg-surface text-ink hover:bg-surface-subtle inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden /> Upload CSV
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void f.text().then(read);
                e.target.value = '';
              }}
            />
            {parsed && (
              <span className="text-ink-muted text-[12px] font-medium">
                {parsed.lines.length} {parsed.lines.length === 1 ? 'parcel' : 'parcels'} read · by{' '}
                <b>{parsed.columns.ref}</b>
                {parsed.columns.amount ? (
                  <>
                    {' '}
                    · amount from <b>{parsed.columns.amount}</b>
                  </>
                ) : (
                  ' · no amount column, amounts not checked'
                )}
                {parsed.skipped.length > 0 && (
                  <span className="text-rose-700"> · {parsed.skipped.length} unreadable</span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-ink mb-1.5 block text-[12px] font-bold" htmlFor="cod-reference">
              Payment reference
            </label>
            <input
              id="cod-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="CRF ID or bank UTR"
              maxLength={100}
              className="border-border bg-surface text-ink h-10 w-full rounded-xl border px-3 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            <p className="text-ink-muted mt-1 text-[11.5px] font-medium">
              Saved on every order this payment covers.
            </p>
          </div>
          <button
            type="button"
            disabled={!parsed?.lines.length || run.isPending}
            onClick={() => run.mutate(false)}
            className="bg-ink hover:bg-ink/90 h-10 w-full rounded-xl text-[12.5px] font-bold text-white disabled:opacity-50"
          >
            {run.isPending && !result ? 'Checking…' : 'Check against orders'}
          </button>
        </div>
      </div>

      {run.isError && (
        <p className="text-[12px] font-semibold text-rose-700">
          {run.error?.message ?? 'Could not check the statement.'}
        </p>
      )}

      {result && (
        <div className="border-border/60 bg-surface overflow-hidden rounded-xl border">
          <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <p className="text-ink text-[13px] font-bold">
              {applied > 0 ? (
                <span className="text-emerald-800">
                  {applied} {applied === 1 ? 'order' : 'orders'} marked received
                </span>
              ) : (
                <>
                  {result.summary.matched} of {result.summary.lines} lines match ·{' '}
                  <span className="text-emerald-800">
                    {formatPaise(result.summary.matchedPaise)}
                  </span>
                </>
              )}
              {result.summary.problems > 0 && (
                <span className="text-rose-700"> · {result.summary.problems} need a look</span>
              )}
            </p>
            {canApply && (
              <button
                type="button"
                onClick={() => run.mutate(true)}
                disabled={run.isPending}
                className="h-9 rounded-lg bg-emerald-600 px-4 text-[12.5px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {run.isPending
                  ? 'Marking…'
                  : `Mark ${result.summary.matched} received · ${formatPaise(result.summary.matchedPaise)}`}
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead className="text-ink-subtle bg-surface sticky top-0 text-[11px] font-semibold uppercase tracking-wider">
                <tr className="border-border/60 border-b">
                  <th className="px-4 py-2.5">Parcel</th>
                  <th className="px-4 py-2.5">Order</th>
                  <th className="px-4 py-2.5">Seller</th>
                  <th className="px-4 py-2.5 text-right">Statement</th>
                  <th className="px-4 py-2.5 text-right">We are owed</th>
                  <th className="px-4 py-2.5">Result</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => {
                  const o = OUTCOME[r.outcome];
                  return (
                    <tr key={`${r.ref}-${i}`} className="border-border/40 border-b last:border-0">
                      <td className="text-ink px-4 py-2.5 font-mono">{r.ref}</td>
                      <td className="text-ink px-4 py-2.5 font-mono">{r.orderNumber ?? '—'}</td>
                      <td className="text-ink px-4 py-2.5">{r.vendorName ?? '—'}</td>
                      <td className="text-ink px-4 py-2.5 text-right tabular-nums">
                        {r.amountPaise !== null ? formatPaise(r.amountPaise) : '—'}
                      </td>
                      <td className="text-ink px-4 py-2.5 text-right font-bold tabular-nums">
                        {r.expectedPaise !== null ? formatPaise(r.expectedPaise) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <StagePill tone={r.applied ? 'emerald' : o.tone}>
                            {r.applied ? 'Marked received' : o.label}
                          </StagePill>
                          {!r.applied && (
                            <span className="text-ink-muted text-[11.5px]">{o.hint}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The COD legs themselves, with the actions that free them.
 *
 * Marking cash received is decided against a courier's statement, so the
 * table shows the AWB the statement names each parcel by, and the reconciler
 * above it matches a whole statement at once.
 */
export function CodRemittancePanel({
  items,
  total,
  isLoading,
  onMarkRemitted,
  onReconciled,
  markPending,
  markError,
}: {
  items: CodRemittanceItem[];
  total: number;
  isLoading: boolean;
  onMarkRemitted: (subOrderId: string) => void;
  onReconciled: () => void;
  markPending: boolean;
  markError: string | null;
}) {
  const [reconciling, setReconciling] = useState(false);
  if (!isLoading && total === 0 && !reconciling) return null;

  return (
    <section
      aria-label="COD awaiting courier remittance"
      className="relative overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-sm"
    >
      <span className="absolute inset-x-0 top-0 h-1 bg-orange-500" aria-hidden />
      <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-orange-100 text-orange-700">
            <Truck className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h2 className="text-ink text-[14px] font-bold">COD awaiting courier remittance</h2>
            <p className="text-ink-muted mt-0.5 max-w-2xl text-[12.5px] font-medium">
              Cash collected at the door that the courier has not yet paid us. A seller cannot be
              settled for these orders until the money is marked received.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StagePill tone="orange">{total} pending</StagePill>
          <button
            type="button"
            onClick={() => setReconciling((v) => !v)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[12px] font-bold ${
              reconciling
                ? 'border-border text-ink hover:bg-surface-subtle border bg-white'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {reconciling ? (
              <>
                <X className="h-3.5 w-3.5" aria-hidden /> Close
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden /> Match a statement
              </>
            )}
          </button>
        </div>
      </div>

      {reconciling && <StatementReconciler onDone={onReconciled} />}

      {isLoading ? (
        <div className="space-y-2 p-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : total === 0 ? (
        <p className="px-5 py-6 text-center text-[13px] font-semibold text-emerald-800">
          Nothing left to reconcile — every delivered COD order has been paid for.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead className="text-ink-subtle border-border/60 border-b text-[11px] font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">AWB</th>
                <th className="px-5 py-3">Seller</th>
                <th className="px-5 py-3">Delivered</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-border/40 border-b last:border-0">
                  <td className="text-ink px-5 py-3 font-mono font-medium">{r.orderNumber}</td>
                  <td className="px-5 py-3">
                    <span className="text-ink font-mono">{r.awbNumber ?? '—'}</span>
                    {r.courierName && (
                      <span className="text-ink-muted block text-[11.5px]">{r.courierName}</span>
                    )}
                  </td>
                  <td className="text-ink px-5 py-3">{r.vendorName}</td>
                  <td className="text-ink-subtle px-5 py-3">
                    {r.deliveredAt ? new Date(r.deliveredAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums text-orange-900">
                    {formatPaise(r.amountPaise)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      className="h-9 rounded-lg bg-emerald-600 px-3 text-[12px] font-bold text-white shadow-sm transition-all hover:bg-emerald-700 disabled:opacity-50"
                      onClick={() => onMarkRemitted(r.subOrderId)}
                      disabled={markPending}
                    >
                      Mark received
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {markError && (
        <p className="border-border/60 border-t px-5 py-3 text-[12px] font-semibold text-red-600">
          {markError}
        </p>
      )}
    </section>
  );
}
